import React, { useEffect, useState } from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import ScienceBackground from '../../components/layout/ScienceBackground';
import API_BASE_URL from '../../config/api';

const Feedback = () => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '',
    registration_id: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  
  const [approvedFeedbacks, setApprovedFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchApprovedFeedbacks();
  }, []);

  const fetchApprovedFeedbacks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/get_public_feedbacks.php`);
      const result = await response.json();
      if (result.status === 'success') {
        setApprovedFeedbacks(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch feedbacks');
    } finally {
      setLoadingFeedbacks(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      setErrorMsg('Please select a rating before submitting.');
      return;
    }
    if (!formData.name.trim() || !formData.message.trim()) {
      setErrorMsg('Please fill in your name and message.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const response = await fetch(`${API_BASE_URL}/submit_feedback.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, rating })
      });
      const result = await response.json();

      if (result.status === 'success') {
        setSuccessMsg('Thank you! Your feedback has been submitted successfully and is pending approval.');
        setFormData({ name: '', registration_id: '', message: '' });
        setRating(0);
      } else {
        setErrorMsg(result.message || 'Failed to submit feedback.');
      }
    } catch (error) {
      setErrorMsg('Network error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStars = (ratingValue) => {
    return Array.from({ length: 5 }).map((_, idx) => (
      <span key={idx} className={idx < ratingValue ? "text-amber-400" : "text-slate-200"}>★</span>
    ));
  };

  return (
    <div className="font-sans bg-slate-50 min-h-screen text-slate-800 overflow-x-hidden selection:bg-rose-500 selection:text-white relative flex flex-col">
      <ScienceBackground />
      <Navbar />
      
      <div className="relative z-10 flex-grow pt-32 pb-24 px-6 md:px-12 max-w-7xl mx-auto w-full">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-600 font-bold text-sm tracking-wide mb-4 border border-emerald-200">
            We Value Your Voice 💬
          </div>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-slate-800 mb-4 drop-shadow-sm font-sans">
            Student <span className="text-emerald-500">Feedback</span>
          </h1>
          <p className="text-lg text-slate-500 font-medium max-w-xl mx-auto">
            See what others are saying and let us know how your learning journey is going.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column: Approved Feedbacks Feed */}
          <div className="lg:col-span-9 space-y-6">
            <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2 mb-6">
              <span className="text-emerald-500">Student</span> Stories
            </h2>
            
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
              {loadingFeedbacks ? (
                [1, 2, 3].map((i) => (
                  <div key={i} className="h-32 bg-white/50 rounded-2xl animate-pulse border border-slate-100"></div>
                ))
              ) : approvedFeedbacks.length === 0 ? (
                <div className="text-center py-12 bg-white/50 rounded-2xl border border-slate-100">
                  <span className="text-3xl mb-3 block">🌟</span>
                  <p className="text-slate-500 font-medium">Be the first to share your experience!</p>
                </div>
              ) : (
                approvedFeedbacks.map((fb, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-bold text-slate-800">{fb.name}</h3>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">
                          {new Date(fb.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="text-sm tracking-widest flex">
                        {renderStars(fb.rating)}
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 italic leading-relaxed">"{fb.message}"</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Column: Submission Form */}
          <div className="lg:col-span-3 bg-white/80 backdrop-blur-xl rounded-3xl p-6 shadow-[0_8px_40px_rgb(0,0,0,0.08)] border border-slate-100 relative lg:sticky lg:top-32">
            <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent rounded-3xl pointer-events-none"></div>
            
            <form onSubmit={handleSubmit} className="relative z-10 space-y-5">
              
              {successMsg && (
                <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-2xl text-sm font-bold text-center">
                  {successMsg}
                </div>
              )}
              
              {errorMsg && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl text-sm font-bold text-center">
                  {errorMsg}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 ml-1">Full Name <span className="text-red-500">*</span></label>
                  <input 
                    type="text" 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="John Doe" 
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all placeholder:text-slate-400 text-[13px]" 
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-700 ml-1">Reg ID / Subject</label>
                  <input 
                    type="text" 
                    value={formData.registration_id}
                    onChange={(e) => setFormData({...formData, registration_id: e.target.value})}
                    placeholder="Bio 2024" 
                    className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all placeholder:text-slate-400 text-[13px]" 
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 ml-1">Rate Your Experience <span className="text-red-500">*</span></label>
                <div className="flex gap-1 p-2 w-fit bg-slate-50 rounded-xl border border-slate-200">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      type="button"
                      key={star}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="text-xl sm:text-2xl focus:outline-none transform hover:scale-110 transition-transform px-1"
                    >
                      <span className={`transition-colors duration-200 ${(hoverRating || rating) >= star ? 'text-amber-400 drop-shadow-md' : 'text-slate-200'}`}>★</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 ml-1">Your Thoughts <span className="text-red-500">*</span></label>
                <textarea 
                  rows="3" 
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="Tell us what you loved or what we can do better..." 
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all placeholder:text-slate-400 resize-none text-[13px]"
                ></textarea>
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting}
                className={`w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/20 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/30 transition-all active:scale-95 text-sm ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Feedback;
